import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import * as adminService from '../services/adminService';
import '../styles/AdminPanel.css'; // We will reuse and update this stylesheet
import { FaShieldAlt, FaExclamationTriangle, FaUsers, FaTrash, FaSync, FaCheckCircle, FaTimesCircle, FaUserLock, FaUserPlus, FaWrench, FaMapMarkerAlt, FaLightbulb } from 'react-icons/fa';
import LocationManagement from './LocationManagement';

// A reusable component for each action button
const ActionButton = ({ onClick, label, icon, isLoading, variant = 'default' }) => (
    <button onClick={onClick} disabled={isLoading} className={`action-button button-${variant}`}>
        {icon}
        <span>{label}</span>
        {isLoading && <div className="spinner" />}
    </button>
);
  
// Component for the "Suggestion Review" section
const SuggestionReview = () => {
    const { idToken: token } = useAuth();
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchSuggestions = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            const fetchedSuggestions = await adminService.getPendingSuggestions(token);
            setSuggestions(fetchedSuggestions);
        } catch (err) {
            setError(err.message || 'Failed to fetch suggestions.');
        } finally {
            setIsLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchSuggestions();
    }, [fetchSuggestions]);

    const handleApprove = async (suggestionId) => {
        try {
            await adminService.approveSuggestion(token, suggestionId);
            setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
        } catch (err) {
            setError(err.message || 'Failed to approve suggestion.');
        }
    };

    const handleReject = async (suggestionId) => {
        try {
            await adminService.rejectSuggestion(token, suggestionId);
            setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
        } catch (err) {
            setError(err.message || 'Failed to reject suggestion.');
        }
    };

    return (
        <div className="admin-section">
            <h3 className="section-header"><FaLightbulb /> Location Suggestions</h3>
            <p className="section-description">
                Review and approve or reject location suggestions submitted by verified students.
            </p>
            {isLoading && <div className="spinner-container"><div className="spinner" /></div>}
            {error && <div className="message-box message-error">{error}</div>}
            {!isLoading && !error && (
                <div className="suggestions-list">
                    {suggestions.length === 0 ? (
                        <p>No pending suggestions.</p>
                    ) : (
                        suggestions.map(suggestion => (
                            <div key={suggestion.id} className="suggestion-card">
                                <h4>{suggestion.name} ({suggestion.city})</h4>
                                <p><strong>Address:</strong> {suggestion.address}</p>
                                <p><strong>Category:</strong> {suggestion.category}</p>
                                <p><strong>Short Description:</strong> {suggestion.shortDescription}</p>
                                <p><strong>Reason:</strong> {suggestion.longDescription}</p>
                                <p className="submitted-by">
                                    Submitted by: {suggestion.displayUsername || suggestion.submittedBy} on {suggestion.submittedAt ? new Date(suggestion.submittedAt._seconds * 1000).toLocaleDateString() : 'Date not available'}
                                </p>
                                <div className="suggestion-actions">
                                    <button onClick={() => handleApprove(suggestion.id)} className="action-button-sm action-approve">
                                        <FaCheckCircle /> Approve
                                    </button>
                                    <button onClick={() => handleReject(suggestion.id)} className="action-button-sm action-reject">
                                        <FaTimesCircle /> Reject
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

// Component for the "User Management" section
const UserManagement = () => {
    const { idToken: token } = useAuth();
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'banned', 'admin', 'verified'

    const fetchUsers = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            const fetchedUsers = await adminService.getUsers(token);
            setUsers(fetchedUsers.sort((a, b) => new Date(b.creationTime) - new Date(a.creationTime)));
        } catch (err) {
            setError(err.message || 'Failed to fetch users.');
        } finally {
            setIsLoading(false);
    }
    }, [token]);

  useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleToggleAdmin = async (userId, currentStatus) => {
        const originalUsers = [...users];
        
        // Optimistic UI update
        setUsers(prevUsers => prevUsers.map(u => u.uid === userId ? { ...u, isAdmin: !currentStatus } : u));
        
        try {
            await adminService.toggleUserAdminStatus(userId, token);
        } catch (err) {
            setError(err.message || 'Failed to toggle admin status.');
            setUsers(originalUsers);
        }
    };

    const handleToggleVerified = async (userId, currentStatus) => {
        const originalUsers = [...users];
        setUsers(prevUsers => prevUsers.map(u => u.uid === userId ? { ...u, is_verified_student: !currentStatus } : u));
    try {
            await adminService.toggleUserVerifiedStatus(userId, token);
        } catch (err) {
            setError(err.message || 'Failed to toggle verified status.');
            setUsers(originalUsers);
        }
    };

    const handleToggleDisabled = async (userId, currentStatus) => {
        const originalUsers = [...users];
        setUsers(prevUsers => prevUsers.map(u => u.uid === userId ? { ...u, disabled: !currentStatus } : u));
        try {
            await adminService.toggleUserDisabledStatus(userId, token);
        } catch (err) {
            setError(err.message || 'Failed to toggle ban status.');
            setUsers(originalUsers);
        }
  };
  
    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Are you sure you want to permanently delete this user? This action cannot be undone.')) {
            return;
        }
        const originalUsers = [...users];
        setUsers(prevUsers => prevUsers.filter(u => u.uid !== userId));
        try {
            await adminService.deleteUser(userId, token);
        } catch (err) {
            setError(err.message || 'Failed to delete user.');
            setUsers(originalUsers);
        }
    };
    
    const filteredUsers = useMemo(() => users.filter(user => {
        const nameMatch = (user.displayName?.toLowerCase() || '').includes(filter.toLowerCase());
        const emailMatch = (user.email?.toLowerCase() || '').includes(filter.toLowerCase());
        const textMatch = nameMatch || emailMatch;

        if (statusFilter === 'all') return textMatch;
        if (statusFilter === 'active') return textMatch && !user.disabled;
        if (statusFilter === 'banned') return textMatch && user.disabled;
        if (statusFilter === 'admin') return textMatch && user.isAdmin;
        if (statusFilter === 'verified') return textMatch && user.is_verified_student;
        return textMatch;
    }), [users, filter, statusFilter]);

    return (
        <div className="admin-section">
            <h3 className="section-header"><FaUsers /> User Management</h3>
            <p className="section-description">
                Manage user roles and permissions. The "admin" role grants access to this panel.
            </p>
            <div className="user-management-controls">
                <input
                    type="text"
                    placeholder="Filter by name or email..."
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                    className="filter-input"
                />
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="filter-select">
                    <option value="all">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="banned">Banned</option>
                    <option value="admin">Admins</option>
                    <option value="verified">Verified</option>
                    </select>
                </div>
            {isLoading && <div className="spinner-container"><div className="spinner" /></div>}
            {error && <div className="message-box message-error">{error}</div>}
            {!isLoading && !error && (
                <div className="user-table-container">
                    <table className="user-table">
                        <thead>
                            <tr>
                                <th>Display Name</th>
                                <th>Email</th>
                                <th>Admin</th>
                                <th>Verified</th>
                                <th>Status</th>
                                <th>Joined</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.map(user => (
                                <UserTableRow 
                                    key={user.uid} 
                                    user={user} 
                                    onToggleAdmin={handleToggleAdmin}
                                    onToggleVerified={handleToggleVerified}
                                    onToggleDisabled={handleToggleDisabled}
                                    onDeleteUser={handleDeleteUser}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
                        </div>
    );
};

const UserTableRow = ({ user, onToggleAdmin, onToggleVerified, onToggleDisabled, onDeleteUser }) => {
    const { currentUser } = useAuth();
    const isCurrentUser = user.uid === currentUser.uid;

    return (
        <tr className={user.disabled ? 'user-row-disabled' : ''}>
            <td>{user.displayName}</td>
            <td>{user.email}</td>
            <td>{user.isAdmin ? <FaCheckCircle className="status-icon-success" /> : <FaTimesCircle className="status-icon-muted" />}</td>
            <td>{user.is_verified_student ? <FaCheckCircle className="status-icon-success" /> : <FaTimesCircle className="status-icon-muted" />}</td>
            <td>
                {user.disabled 
                    ? <span className="status-badge status-banned">Banned</span>
                    : <span className="status-badge status-active">Active</span>
                }
            </td>
            <td>{new Date(user.creationTime).toLocaleDateString()}</td>
            <td>
                <div className="action-buttons-group">
                    <button 
                        className="action-button-sm"
                        onClick={() => onToggleAdmin(user.uid, user.isAdmin)}
                        disabled={isCurrentUser}
                        title={isCurrentUser ? "Cannot change your own admin status" : "Toggle Admin Status"}
                    >
                        {user.isAdmin ? <FaShieldAlt/> : <FaShieldAlt/>}
                    </button>
                    <button 
                        className="action-button-sm"
                        onClick={() => onToggleVerified(user.uid, user.is_verified_student)}
                        title="Toggle Verified Status"
                    >
                        {user.is_verified_student ? <FaCheckCircle /> : <FaTimesCircle />}
                    </button>
                    <button 
                        className={`action-button-sm ${user.disabled ? 'action-unban' : 'action-ban'}`}
                        onClick={() => onToggleDisabled(user.uid, user.disabled)}
                        disabled={isCurrentUser}
                        title={isCurrentUser ? "Cannot ban yourself" : (user.disabled ? "Unban User" : "Ban User")}
                    >
                        {user.disabled ? <FaUserPlus /> : <FaUserLock />}
                    </button>
                    <button 
                        className="action-button-sm action-delete"
                        onClick={() => onDeleteUser(user.uid)}
                        disabled={isCurrentUser}
                        title={isCurrentUser ? "Cannot delete yourself" : "Delete User"}
                    >
                        <FaTrash />
                    </button>
                </div>
            </td>
        </tr>
    );
};

// Component for the "Dangerous Actions" section
const DangerousActions = () => {
    const { idToken: token } = useAuth();
    const [isLoading, setIsLoading] = useState(null); // e.g., 'cleanup', 'import'
    const [message, setMessage] = useState({ text: '', type: '' });

    const handleAction = async (action, actionFn, successMessage) => {
        if (!window.confirm(`Are you sure you want to proceed with this action: ${action.toUpperCase()}? This cannot be undone.`)) {
            return;
        }
        
        setIsLoading(action);
        setMessage({ text: '', type: '' });
        
        try {
            const result = await actionFn(token);
            setMessage({ text: result.message || successMessage, type: 'success' });
        } catch (error) {
            setMessage({ text: error.message || `Failed to perform ${action}.`, type: 'error' });
        } finally {
            setIsLoading(null);
        }
    };

    return (
        <div className="admin-section">
            <h3 className="section-header"><FaExclamationTriangle /> Dangerous Actions</h3>
            <p className="section-description">
                These actions directly manipulate the database. They are powerful and should be used with caution.
            </p>
            <div className="actions-grid">
                 {/* The buttons for import, cleanup, and update have been removed 
                     as their backend routes were intended for one-time setup and are a security risk. 
                     This section is kept for any future admin tools. */}
            </div>
            {message.text && (
                <div className={`message-box message-${message.type}`}>
                    {message.text}
                </div>
            )}
        </div>
    );
};

// Main Admin Panel Component
const AdminPanel = () => {
    const { currentUser, loading: authLoading } = useAuth();
    const [activeView, setActiveView] = useState('users'); // 'users', 'locations', 'suggestions', 'tools'
    const [isAdmin, setIsAdmin] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!authLoading) {
            setIsAdmin(currentUser?.isAdmin || false);
            setIsLoading(false);
        }
    }, [currentUser, authLoading]);

    const renderActiveView = () => {
        if (!isAdmin) return null;

        switch (activeView) {
            case 'users':
                return <UserManagement />;
            case 'locations':
                return <LocationManagement />;
            case 'suggestions':
                return <SuggestionReview />;
            case 'tools':
                return <DangerousActions />;
            default:
                return <UserManagement />;
        }
    };

    if (isLoading) {
        return <div className="loading-overlay"><div className="spinner" /> Verifying access...</div>;
    }

    if (!isAdmin) {
        return (
            <div className="admin-panel-container access-denied">
                <h2>Access Denied</h2>
                <p>You do not have permission to view this page.</p>
            </div>
        );
    }
    
    return (
        <div className="admin-panel-container">
            <aside className="admin-sidebar">
                <div className="sidebar-header">
                    <FaShieldAlt />
                    <h2>Admin Panel</h2>
                </div>
                <nav className="sidebar-nav">
                    <button onClick={() => setActiveView('users')} className={activeView === 'users' ? 'active' : ''}>
                        <FaUsers /> Users
                    </button>
                    <button onClick={() => setActiveView('locations')} className={activeView === 'locations' ? 'active' : ''}>
                        <FaMapMarkerAlt /> Locations
                    </button>
                    <button onClick={() => setActiveView('suggestions')} className={activeView === 'suggestions' ? 'active' : ''}>
                        <FaLightbulb /> Suggestions
                    </button>
                    <button onClick={() => setActiveView('tools')} className={activeView === 'tools' ? 'active' : ''}>
                        <FaWrench /> Tools
                    </button>
                </nav>
            </aside>
            <main className="admin-main-content">
                {renderActiveView()}
            </main>
        </div>
    );
};

export default AdminPanel;